import type { StyleSystemProps } from '@withjoy/joykit';
import { Box, styled } from '@withjoy/joykit';
import type { CSSObject } from '@withjoy/joykit/components/Box/Box.types';
import { breakpointAliasPx, pxToRem } from '@withjoy/joykit/theme';
import { PRODUCT_GRID_BREAKPOINTS, REFINEMENT_PANEL_STICKY_THRESHOLD_CSS_VAR, REFINEMENT_PANEL_TOGGLE_TOP_CSS_VAR } from '../../Catalog.constants';

export const StyledRoot = styled(Box)<{ $isAdminView: boolean }>`
  ${props => ({
    [REFINEMENT_PANEL_STICKY_THRESHOLD_CSS_VAR]: props.$isAdminView ? '58px' : '168px',
    [REFINEMENT_PANEL_TOGGLE_TOP_CSS_VAR]: props.$isAdminView ? '86px' : '0px',
  })};
`;

export const StyledWrapper = styled(Box)``;

export const StyledMain = styled(Box)``;

const skeletonRootStyles: StyleSystemProps = { display: 'flex', justifyContent: 'center', flexWrap: 'wrap' };

const rootStyles: StyleSystemProps = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  alignSelf: 'center',
  alignItems: 'center',
};

const wrapperStyles = ({ isEmptyState, maxColumns }: { isEmptyState: boolean; maxColumns?: MaxColumnType }): StyleSystemProps => ({
  width: {
    _: '100%',
    // the breakpoint is also its own upper width cap
    [PRODUCT_GRID_BREAKPOINTS[3]]: maybeUseValue(maxColumns !== 3, breakpointAliasPx[PRODUCT_GRID_BREAKPOINTS[3]]),
  },
  maxWidth: '100%',
  display: 'flex',
  paddingX: isEmptyState
    ? 0
    : {
        _: 3,
        [PRODUCT_GRID_BREAKPOINTS[1]]: 7,
      },
});

const mainStyles: StyleSystemProps = {
  flex: '1 auto',
};

function maybeUseValue<Value>(cond: boolean, val: Value): Value | undefined {
  return cond ? val : undefined;
}

// we've changed our minds on the max number of columns we'll present several times;
//   currently, the max is 4 columns.
//   back in the day when 4+ was allowed, logic was put in place to enforce a max of 4.
//   those `maxColumns` call-outs have been preserved in case we change our mind again.
//   the { maxColumns: 3 } logic below preserves a practical example of enforcement.
export type MaxColumnType = 3 | 4 | 'default';

const nonZeroProductListStyles = (maxColumns?: MaxColumnType): CSSObject => ({
  display: 'grid',
  // Base grid styling
  gridColumnGap: {
    _: 3,
    [PRODUCT_GRID_BREAKPOINTS[1]]: 10,
  },
  gridRowGap: {
    _: 5,
    [PRODUCT_GRID_BREAKPOINTS[1]]: 10,
  },
  gridTemplateColumns: {
    // NOTE: for the future, never render more than 4 columns
    //   also ... there's tight coupling between these breakpoints and:
    //   - subCatHighlightsIndex in 'useProductGridHighlights'
    //   - TODO:  fill in more as we discover them
    _: 'repeat(2, 1fr)',
    [PRODUCT_GRID_BREAKPOINTS[1]]: 'repeat(3, minmax(0, 1fr))',
    [PRODUCT_GRID_BREAKPOINTS[2]]: maybeUseValue(maxColumns !== 3, 'repeat(4, minmax(0, 1fr))'),
  },
  gridTemplateRows: '1fr',

  paddingY: {
    _: 5,
    [PRODUCT_GRID_BREAKPOINTS[1]]: 7,
  },

  // Grid dividers
  ['& > div']: {
    // Base Row divider
    _after: {
      content: '" "',
      height: '1px',
      width: '100%',
      bottom: {
        _: pxToRem(-8),
        [PRODUCT_GRID_BREAKPOINTS[1]]: pxToRem(-32),
      },
      backgroundColor: 'mono3',
      position: 'absolute',
    },

    // Base Column divider
    _before: {
      position: 'absolute',
      width: '1px',
      height: {
        _: `calc(100% + ${pxToRem(16)})`, // 16px, aka row gap x2,
        [PRODUCT_GRID_BREAKPOINTS[1]]: `calc(100% + ${pxToRem(64)})`,
      },
      top: '50%',
      right: {
        _: pxToRem(-1 * ((8 - 1) / 2 + 1)), // 8 = column gap; - 1 = divider width; + 1 = to center
        [PRODUCT_GRID_BREAKPOINTS[1]]: pxToRem(-32), // can be less precise at scale
      },
      backgroundColor: 'mono3',
      transform: 'translateY(-50%)',
    },

    // Show/hide column rules
    _odd: {
      _before: {
        // Default to show
        content: '" "',
      },
    },
    _even: {
      _before: {
        content: {
          // Reveal when 3+ column
          [PRODUCT_GRID_BREAKPOINTS[1]]: '" "',
        },
      },
    },
    // Targeting 3rd column
    ['&:nth-child(3n+3)']: {
      _before: {
        content: {
          // Hide it when grid is 3 column
          [PRODUCT_GRID_BREAKPOINTS[1]]: 'unset',
          // Reveal when grid is 4+ column
          [PRODUCT_GRID_BREAKPOINTS[2]]: maybeUseValue(maxColumns !== 3, '" "'),
        },
      },
    },

    // NOTE: Commenting out until discussing with merch about 4 columns
    // Targeting 4th column
    ['&:nth-child(4n+4)']: {
      _before: {
        content: {
          // Hide when grid is 4 column
          [PRODUCT_GRID_BREAKPOINTS[2]]: 'unset',
        },
      },
    },
  },
});

const emptyProductListStyles: StyleSystemProps = {
  marginTop: '7px',
};

export const StyledGridContainer = styled(Box)<{ isMobile?: boolean }>`
  display: grid;
`;

export const StyledActiveFiltersContainer = styled(Box)<{ isMobile?: boolean }>``;

export const styles = {
  root: rootStyles,
  skeletonRoot: skeletonRootStyles,
  wrapper: wrapperStyles,
  main: mainStyles,

  // product grid
  nonZeroProductList: nonZeroProductListStyles,
  emptyProductList: emptyProductListStyles,
};
